import { useEffect, useState } from 'react';

import config from '@/config';
import useCache from '@/hooks/useCache';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import UserService from '@/services/user.service';
import AddShippingAddress from '@/shared-components/forms/components/AddShippingAddress';
import SettingsLoadingPage from '@/shared-components/loaders/components/SettingsLoadingPage';
import ConfirmationDialog from '@/shared-components/popups/components/ConfirmationDialog';
import SettingsHeading from '@/shared-components/text/components/SettingsHeading';
import Text from '@/shared-components/text/components/Text';

import AddressCard from './components/AddressCard';

import './index.css';

export default function Address() {
  const { state, stateHandler } = useStateHandler();
  const t = useTranslations('common.user_settings.address');
  const { loading, fetchData, setFetchData } = useCache(`${state.user_session._id}_addresses`, `${config.apis.api.url}/address`, {
    injectToken: true
  });
  const [openNewAddressForm, setOpenNewAddressForm] = useState(false);
  const [addressToEdit, setAddressToEdit] = useState(null);
  const [addressToRemove, setAddressToRemove] = useState(null);
  const [preferredAddressId, setPreferredAddressId] = useState(state.user_session?.preferred_shipping_address_id ?? null);
  const sessionPreferredId = state.user_session?.preferred_shipping_address_id ?? null;
  const addresses = Array.isArray(fetchData) ? fetchData : [];

  // Centralized helper to sync the preferred id across Redux/session and the API.
  function persistPreferredAddress(addressId) {
    const normalizedId = addressId ?? null;

    if ((state.user_session?.preferred_shipping_address_id ?? null) !== normalizedId) {
      stateHandler.set("user_session.preferred_shipping_address_id", normalizedId);
    }

    if (typeof UserService.setPreferredShippingAddress === 'function') {
      UserService.setPreferredShippingAddress(normalizedId).catch((error) => {
        console.error('persistPreferredAddress error', error);
      });
    }
  }

  // Keep the preferred address in sync with the API payload and the stored session flag.
  useEffect(() => {
    if (addresses.length === 0) {
      if (preferredAddressId !== null) {
        setPreferredAddressId(null);
      }
      if (sessionPreferredId !== null) {
        persistPreferredAddress(null);
      }
      return;
    }

    const currentPreferredStillPresent = preferredAddressId
      && addresses.some(address => address._id === preferredAddressId);

    if (currentPreferredStillPresent) {
      return;
    }

    const sessionPreferredStillPresent = sessionPreferredId
      && addresses.some(address => address._id === sessionPreferredId);

    const flaggedAddress = addresses.find(address => (
      address?.address_is_default
      || address?.address_is_preferred
      || address?.isPreferred
      || address?.preferred
    ));

    const fallbackAddress = sessionPreferredStillPresent
      ? addresses.find(address => address._id === sessionPreferredId)
      : flaggedAddress ?? addresses[addresses.length - 1];

    const fallbackId = fallbackAddress?._id ?? null;

    if (fallbackId !== preferredAddressId) {
      setPreferredAddressId(fallbackId);
    }

    if (fallbackId !== sessionPreferredId) {
      persistPreferredAddress(fallbackId);
    }
  }, [fetchData, preferredAddressId, sessionPreferredId]);

  function addShippingAddress(newAddressData) {
    const updatedAddresses = [
      ...addresses,
      newAddressData
    ];
    setFetchData(updatedAddresses);
    if (newAddressData?._id) {
      setPreferredAddressId(newAddressData._id);
      persistPreferredAddress(newAddressData._id);
    }
    stateHandler.set("temporalPopup", {
      text: t('popups.address_added'),
    });
  }

  function editShippingAddress(newAddressData) {
    const updatedAddresses = addresses.map(address => {
      if(address._id === newAddressData._id) {
        return newAddressData;
      } else {
        return address;
      }
    });
    setFetchData(updatedAddresses);
    if (newAddressData?._id && preferredAddressId === newAddressData._id) {
      setPreferredAddressId(newAddressData._id);
      persistPreferredAddress(newAddressData._id);
    }
    stateHandler.set("temporalPopup", {
      text: t('popups.address_updated'),
    });
  }

  function removeShippingAddress() {
    const removedId = addressToRemove?._id;
    const updatedAddresses = addresses.filter(address => (
      address._id !== removedId
    ));
    UserService.deleteShippingAddress(addressToRemove);
    setFetchData(updatedAddresses);
    if (removedId && removedId === preferredAddressId) {
      const fallbackId = updatedAddresses.length > 0
        ? updatedAddresses[updatedAddresses.length - 1]._id
        : null;
      setPreferredAddressId(fallbackId);
      persistPreferredAddress(fallbackId);
    }
    stateHandler.set("temporalPopup", {
      text: t('popups.address_removed'),
    });
    setAddressToRemove(null);
  }

  function handlePreferredAddressSelection(address) {
    if (!address?._id || address._id === preferredAddressId) {
      return;
    }

    setPreferredAddressId(address._id);
    persistPreferredAddress(address._id);
    stateHandler.set("temporalPopup", {
      text: t('popups.preferred_updated'),
    });
  }

  if(loading) {
    return (
      <SettingsLoadingPage page={t('heading')}/>
    );
  } else {
    return (
      <>
        <SettingsHeading text={t('heading')}/>
        <div className="settings-address__container">
          <Text className="settings-address__heading font-size-large">
            {t('shipping_heading')}
          </Text>
    
          {
            addresses.length === 0 ?
              <Text
                className="settings-address__no-address font-size-small"
                paragraph
                justify>
                {t('empty_state')}
              </Text>
              :
              addresses.map(address => (
                <AddressCard 
                  key={address._id} 
                  addressData={address}
                  isPreferred={address._id === preferredAddressId}
                  onSelectPreferred={handlePreferredAddressSelection}
                  setAddressToEdit={setAddressToEdit}
                  setAddressToRemove={setAddressToRemove}
                />
              ))
          }
    
          <Text
            className="settings-address__add-button element-non-selectable font-size-medium"
            onClick={()=>setOpenNewAddressForm(true)}>
            {t('add_button')}
          </Text>
        </div>
        <AddShippingAddress
          open={openNewAddressForm}
          close={()=>setOpenNewAddressForm(false)}
          onAdd={addShippingAddress}
        />
        <AddShippingAddress
          open={Boolean(addressToEdit)}
          close={()=>setAddressToEdit(null)}
          addressData={addressToEdit}
          onAdd={editShippingAddress}
          editMode
        />
        <ConfirmationDialog
          open={Boolean(addressToRemove)}
          onClose={()=>setAddressToRemove(null)}
          title={t('delete_dialog.title')}
          closeButtonText={t('delete_dialog.cancel_button')}
          confirmButtonText={t('delete_dialog.confirm_button')}
          dialogText={t('delete_dialog.message')}
          onConfirm={removeShippingAddress}
          opacity
        />
      </>
    );
  }
}
